import React from 'react';
import { Avatar, avatarColor, Badge, Alert, Menu, Skeleton, SkeletonRegion } from '@parsina/framework';
import { C } from '../../constants/theme';
import { ROW_PAD } from '../../constants/density';
import { useTranslation } from '../../i18n';
import { useClinicContext, refOf } from '../../context/ClinicContext.jsx';
import { useShortcut, Kbd } from '../../lib/shortcuts.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import MyAccessDrawer from './MyAccessDrawer.jsx';

/**
 * The clinic context switcher — the sidebar header.
 *
 * docs/CLINIC_MULTI_CLINIC_UX_REV2.md §5.2 / §A6. The design decisions worth
 * defending, because each looks like a small choice and is not:
 *
 * IDENTITY IS ALWAYS SHOWN; SELECTION IS NOT. The clinic's name renders for
 * everyone — a receptionist at a single clinic still benefits from seeing whose
 * cash desk they are about to bill against. The chevron, the hover, the focus ring
 * and `Ctrl+K` appear ONLY when there is something to choose between. A chevron on
 * a single-membership account is a lie about what pressing it will do, and the
 * requirement is explicit that a single-clinic person must not be asked to select.
 *
 * IT TAKES THE 68px SLOT, AND THE COLLAPSE TOGGLE MOVES TO THE FOOTER. Both cannot
 * survive the collapsed rail, and switching context is a many-times-a-day action
 * while collapsing is a once-a-session one.
 *
 * AN AVATAR MEANS A PERSON, AND THE RAIL SHOWS EXACTLY ONE. The header used to wear
 * a 36px monogram disc of its own, facing the signed-in person's avatar at the foot
 * of the same rail — two discs of the same weight, which read as two profile
 * pictures. A clinic HAS no picture: there is no logo field anywhere in the
 * platform, so that disc could only ever hold two letters. So when the rail is
 * open the clinic is type — its name, its role line, a chevron — and its colour,
 * seeded by `clinicId`, moves to a 3px bar on the inline-start edge.
 *
 * The monogram survives in the two places it does real work, both of which are
 * about telling several clinics apart rather than decorating one: the menu rows
 * below, and the collapsed 68px rail, where nothing else can name a clinic at all.
 * There it carries the clinic's colour instead of the bar, so each state shows
 * exactly one colour cue.
 *
 * BLOCKED ROWS ARE VISIBLE, DIMMED AND SAY WHY. Hiding them is what makes the
 * doctor app's existing switcher misleading — it offers a choice it cannot make.
 */

const MINI = 68;

/** Two-letter monogram. Persian graphemes, so a name in any script degrades safely. */
function initials(name = '', rtl = true) {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  // i18n-exempt: a GLYPH, not copy — the placeholder mark inside a monogram disc
  // when a clinic has no name yet. It follows the page's script: a Latin "?"
  // inside a Persian avatar would be as wrong as «؟» inside an English one.
  if (!words.length) return rtl ? '؟' : '?';
  if (words.length === 1) return [...words[0]].slice(0, 2).join('');
  return [...words[0]][0] + [...words[1]][0];
}

function RoleLine({ membership, t }) {
  // ONE badge, resolving tier and roles into a single line (§5.2.2). The founder
  // rule matters here: `isFounder` is decided by the clinic's own row, not by which
  // Keycloak role a given app happened to grant, so the same person is never «مالک»
  // in one session and «مدیر» in the next.
  if (membership.isFounder || membership.tier === 'OWNER') {
    return <>{t('clinic.context.tier.owner')}</>;
  }
  if (membership.tier === 'ADMIN') return <>{t('clinic.context.tier.admin')}</>;

  const roles = membership.roles || [];
  if (roles.length === 1) return <>{roles[0].nameFa}</>;
  if (roles.length > 1) {
    return <>{t('clinic.context.roles.more', { name: roles[0].nameFa, count: roles.length - 1 })}</>;
  }
  if (membership.kinds?.includes('PRACTITIONER')) {
    return <>{t('clinic.context.kind.practitioner')}</>;
  }
  // Conspicuous on purpose: this is the exact state that produces a person who can
  // sign in and then see nothing, and they should learn it before their admin does.
  return <span style={{ color: C.amber }}>{t('clinic.context.roles.none')}</span>;
}

function MenuRow({ membership, active, index, onSelect, t, offline }) {
  const blocked = !membership.enterable || offline;
  const reason = membership.blockedReason
    ? t(`clinic.context.blocked.${membership.blockedReason}`)
    : '';

  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      aria-disabled={blocked || undefined}
      data-menu-focus={active ? 'true' : undefined}
      data-testid={`clinic-row-${membership.clinicId}`}
      onClick={() => onSelect(membership)}
      title={blocked ? reason : membership.clinicName}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: ROW_PAD.regular,
        background: active ? C.tealLight : 'transparent',
        border: 'none', borderRadius: 9,
        cursor: blocked ? 'default' : 'pointer',
        textAlign: 'start', fontFamily: 'inherit',
        opacity: blocked ? 0.55 : 1,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.bg; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Seeded by clinic ID, not by name: a chain whose branches share a name
          would otherwise get identical monograms, which is precisely the case the
          colour is there to disambiguate (E19). */}
      <Avatar name={membership.clinicName} size={30} bg={avatarColor(membership.clinicId)} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: 13, fontWeight: 600, color: C.textPrimary,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {membership.clinicName}
        </span>
        <span style={{
          display: 'block', fontSize: 11, fontWeight: 400, color: C.textSecondary, marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {blocked ? reason : <RoleLine membership={membership} t={t} />}
          {/* City disambiguates a chain whose branches share a name — the role
              line cannot, because it is often identical across them. */}
          {!blocked && membership.city ? ` · ${membership.city}` : ''}
        </span>
      </span>
      {membership.repairNeeded ? (
        <Badge variant="warning">{t('clinic.context.repairNeeded')}</Badge>
      ) : null}
      {/* Digits are bound only while the menu is open, so the cap belongs here
          rather than on the trigger. */}
      {!blocked && index != null && index < 9 ? <Kbd combo={String(index + 1)} /> : null}
      {active ? <span aria-hidden style={{ color: C.teal, fontWeight: 900 }}>✓</span> : null}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      // 11 is the ramp's floor. No letter-spacing: tracking pulls Persian
      // letters apart at their joins.
      padding: '8px 12px 4px', fontSize: 11, fontWeight: 700,
      color: C.textMuted,
    }}>
      {children}
    </div>
  );
}

function FooterAction({ onClick, icon, label, count, disabled, hint }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      title={hint || undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: ROW_PAD.regular, background: 'transparent', border: 'none',
        borderRadius: 9, textAlign: 'start',
        // Same size and weight as a clinic row's name: both are the menu's
        // item labels, and a footer label heavier than the rows reads as a heading.
        fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        color: disabled ? C.textMuted : C.textPrimary,
        // One `cursor`, not two. A style object is not a CSS cascade — the
        // second key silently deletes the first, so the unconditional
        // `cursor: 'pointer'` that used to sit above was dead the moment this
        // line was added, and only looked like a fallback.
        cursor: disabled ? 'default' : 'pointer',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = C.bg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span aria-hidden style={{ width: 30, textAlign: 'center', color: C.textSecondary }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block' }}>{label}</span>
        {/* The reason travels in the accessible text, not only in a tooltip: a
            row that is dimmed and silent teaches people to keep pressing it. */}
        {hint ? (
          <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: C.textMuted, marginTop: 2 }}>
            {hint}
          </span>
        ) : null}
      </span>
      {count ? <Badge>{count}</Badge> : null}
    </button>
  );
}

export default function ClinicSwitcher({ collapsed = false }) {
  const { t, isRTL } = useTranslation();
  const navigate = useAppNavigate();
  const {
    memberships, enterable, activeMembership, canSwitch, capabilities,
    switchClinic, switching, pendingInvitations, state,
  } = useClinicContext();

  // «افزودن کلینیک جدید» is for people who already FOUNDED a clinic — founding
  // another is an owner's act. Everyone else reaches their first clinic through
  // «شروع», so the row is absent for them rather than disabled: a receptionist
  // has no use for a greyed-out button about somebody else's decision.
  //
  // For a founder it is live where the platform allows several clinics per
  // account (`multiFounder` = the BFF's CLINIC_MULTI_FOUNDER, which the BFF also
  // ENFORCES in `createClinic`) and disabled-with-a-reason where it does not.
  const alreadyFounder = memberships.some((m) => m.isFounder);
  const showAddClinic = alreadyFounder;
  const cannotAddClinic = alreadyFounder && !capabilities?.multiFounder;

  const [open, setOpen] = React.useState(false);
  const [accessOpen, setAccessOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const triggerRef = React.useRef(null);

  // A switch needs the network, and one attempted without it is guaranteed to hit
  // the revert path — which reads as a broken switcher rather than as a broken
  // connection. So it says so instead of trying.
  //
  // `navigator.onLine` ONLY. The framework's `useOnlineStatus` distinguishes a
  // third state — `degraded`, meaning the browser claims to be online while a
  // captive portal, a dead VPN or a firewall eats every packet — and that is the
  // state this guard would most like to catch, because it is the one that produces
  // a confident-looking failure. It ships in framework v0.8.0 and this app installs
  // 0.4.0, where a named import of a missing export is a hard Parcel failure rather
  // than a soft undefined. Widen this to `useOnlineStatus()` when the pin is
  // bumped; until then the guard is honest about covering only the easy half.
  const [offline, setOffline] = React.useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  React.useEffect(() => {
    const sync = () => setOffline(navigator.onLine === false);
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  // Interactive when there is a real choice — a second clinic to switch to, or the
  // ability to add one. Creating is always available (§2.7), so in practice this is
  // true for everyone; the guard keeps the ONE case the requirement names — a person
  // with a single membership and nothing to add — free of a selection affordance.
  const interactive = canSwitch || enterable.length > 0;

  const others = React.useMemo(
    () => memberships.filter((m) => m.clinicId !== activeMembership?.clinicId),
    [memberships, activeMembership],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim();
    if (!q) return others;
    return others.filter((m) => `${m.clinicName} ${m.city || ''}`.includes(q));
  }, [others, query]);

  const selectable = React.useMemo(() => filtered.filter((m) => m.enterable), [filtered]);

  const close = React.useCallback(() => { setOpen(false); setQuery(''); }, []);

  const onSelect = React.useCallback((m) => {
    if (offline) return;
    if (!m.enterable) {
      // A blocked row is not inert: an invitation or a request is actionable, just
      // not here. Route to the place where it can be acted on rather than doing
      // nothing, which reads as a broken button.
      if (m.blockedReason === 'INVITED' || m.blockedReason === 'REQUESTED') {
        close();
        navigate('invitations');
      }
      return;
    }
    close();
    switchClinic(refOf(m));
  }, [close, navigate, switchClinic, offline]);

  // ── Shortcuts. Every one through the registry, never a window listener. ─────
  useShortcut('ctrl+k', () => setOpen((v) => !v), {
    label: t('shortcuts.labels.switchClinic'),
    group: t('shortcuts.groups.general'),
    enabled: interactive,
  });
  useShortcut('esc', close, { label: t('common.actions.close'), enabled: open });

  // Bare digits, bound ONLY while the menu is open. `Ctrl+1…9` is NOT used: those
  // are the browser's own tab shortcuts on Windows and Linux, and this app
  // deliberately supports keeping two clinics in two tabs — taking the keys that
  // move between them would be taking the wrong ones.
  for (let i = 0; i < 9; i += 1) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- fixed-length loop: the
    // hook count is constant across renders, which is what the rule protects.
    useShortcut(String(i + 1), () => {
      const target = selectable[i];
      if (target) onSelect(target);
    }, { enabled: open, group: t('shortcuts.groups.general') });
  }

  if (state === 'loading' && !activeMembership) {
    // The trigger's own footprint — whichever one the rail is wearing — rather
    // than 68px of nothing: the rail keeps its shape and the clinic's name fills
    // in place. Open, that is two name lines and no mark, because no mark is
    // coming; collapsed, it is the 36px monogram alone. Muted, because the
    // blocks sit on the dark rail and the neutral sheen at full strength reads
    // as a selected row.
    return (
      <SkeletonRegion
        label={t('common.state.loading')}
        data-testid="clinic-switcher-loading"
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 68, opacity: 0.4 }}>
        {collapsed ? (
          <span style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <Skeleton width={36} height={36} radius={10} />
          </span>
        ) : (
          <span style={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6,
            paddingInlineStart: 16, paddingInlineEnd: 10,
          }}>
            <Skeleton width="70%" height={15} />
            <Skeleton width="45%" height={11} />
          </span>
        )}
      </SkeletonRegion>
    );
  }

  const name = activeMembership?.clinicName || t('nav.title.clinicPanelShort');
  const label = interactive
    ? t('clinic.context.triggerSwitch', { name })
    : t('clinic.context.trigger', { name });

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      data-testid="clinic-switcher-trigger"
      aria-haspopup={interactive ? 'menu' : undefined}
      aria-expanded={interactive ? open : undefined}
      aria-label={label}
      title={collapsed ? name : undefined}
      disabled={!interactive}
      onClick={() => interactive && setOpen((v) => !v)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', height: 68, padding: 0,
        background: 'transparent', border: 'none',
        cursor: interactive ? 'pointer' : 'default',
        color: 'inherit', fontFamily: 'inherit', textAlign: 'start',
        overflow: 'hidden', position: 'relative',
      }}
      onMouseEnter={(e) => { if (interactive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* The clinic's colour, on the edge the text starts from. Same seed as the
          menu rows — the id, not the name — so two branches of a chain that share
          a name still differ here. Open rail only: on the 68px rail the monogram
          below carries the colour instead, and two cues would say it twice. */}
      {!collapsed && activeMembership ? (
        <span
          aria-hidden
          style={{
            position: 'absolute', insetBlock: 14, insetInlineStart: 0,
            width: 3, borderRadius: 2,
            background: avatarColor(activeMembership.clinicId),
          }}
        />
      ) : null}
      {collapsed ? (
        <span style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <span
            aria-hidden
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: activeMembership
                ? avatarColor(activeMembership.clinicId)
                : 'rgba(255,255,255,0.16)',
              color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800,
            }}
          >
            {initials(name, isRTL)}
          </span>
        </span>
      ) : (
        <span style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
          // The 68px icon column is gone with the disc, so the name insets from
          // the rail's edge itself — clear of the colour bar, and short of the
          // 10px the nav pills below are inset by, because a heading that lines
          // up with the pills reads as one of them.
          paddingInlineStart: 16,
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{
              display: 'block', color: 'white', fontSize: 15, fontWeight: 800,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {name}
            </span>
            <span style={{
              display: 'block', color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {activeMembership ? <RoleLine membership={activeMembership} t={t} /> : null}
            </span>
          </span>
          {/* The chevron IS the affordance. Rendering it without a menu behind it
              would promise something pressing cannot deliver. */}
          {interactive ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden
              style={{ flexShrink: 0, marginInlineEnd: 10 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          ) : null}
        </span>
      )}
    </button>
  );

  return (
    <>
      {trigger}
      <Menu
        data-testid="clinic-menu"
        open={open && interactive}
        onClose={close}
        anchorRef={triggerRef}
        ariaLabel={t('clinic.context.menuLabel')}
      >
        {/* Search appears only once the list is long enough to need it. Below
            eight rows a search box is one more thing to look past. */}
        {others.length >= 8 ? (
          <input
            autoFocus
            data-menu-focus="true"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('clinic.context.searchPlaceholder')}
            style={{
              width: '100%', padding: '8px 10px', marginBottom: 4,
              border: `1px solid ${C.border}`, borderRadius: 8,
              fontSize: 12.5, fontFamily: 'inherit', outline: 'none',
            }}
          />
        ) : null}

        {offline ? (
          <div style={{ padding: '4px 6px 8px' }}>
            <Alert variant="warning">{t('clinic.context.offline')}</Alert>
          </div>
        ) : null}

        {activeMembership ? (
          <div role="group" aria-label={t('clinic.context.activeSection')}>
            <SectionLabel>{t('clinic.context.activeSection')}</SectionLabel>
            <MenuRow membership={activeMembership} active onSelect={onSelect} t={t} />
            {/* «and what may I do here?» — the question that follows «which clinic
                am I in?», and the half of UX principle 4 the role line alone
                cannot answer. */}
            <button
              type="button"
              role="menuitem"
              data-testid="my-access-link"
              onClick={() => { close(); setAccessOpen(true); }}
              style={{
                display: 'block', width: '100%', textAlign: 'start',
                padding: '2px 12px 8px', marginTop: -4,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: C.teal, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {t('clinic.myAccess.link')}
            </button>
          </div>
        ) : null}

        {filtered.length ? (
          <div role="group" aria-label={t('clinic.context.othersSection')}>
            <SectionLabel>{t('clinic.context.othersSection')}</SectionLabel>
            {filtered.map((m) => (
              <MenuRow
                key={m.clinicId}
                membership={m}
                active={false}
                index={m.enterable ? selectable.indexOf(m) : null}
                onSelect={onSelect}
                t={t}
                offline={offline}
              />
            ))}
          </div>
        ) : null}

        {query && !filtered.length ? (
          <div style={{ padding: '14px 12px', fontSize: 12.5, color: C.textMuted }}>
            {t('clinic.context.noMatches')}
          </div>
        ) : null}

        {switching ? (
          <div style={{ padding: '4px 6px' }}>
            <Alert variant="info">{t('clinic.context.switching')}</Alert>
          </div>
        ) : null}

        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 4, paddingTop: 4 }}>
          {showAddClinic ? (
            <FooterAction
              icon="＋"
              label={t('clinic.context.addClinic')}
              disabled={cannotAddClinic}
              hint={cannotAddClinic ? t('clinic.context.addClinicUnavailable') : undefined}
              onClick={() => { close(); navigate('onboarding'); }}
            />
          ) : null}
          <FooterAction
            icon="✉"
            label={t('clinic.context.invitations')}
            count={pendingInvitations.length}
            onClick={() => { close(); navigate('invitations'); }}
          />
        </div>
      </Menu>

      <MyAccessDrawer open={accessOpen} onClose={() => setAccessOpen(false)} />
    </>
  );
}
